import React from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

/**
 * ProtectedRoute — guards a page by its page key from the Access Panel.
 *
 * Usage:
 *   <ProtectedRoute pageKey="user_management">
 *     <UserManagement />
 *   </ProtectedRoute>
 *
 * - No token           → redirect to login
 * - No pageKey         → any logged-in user can access (e.g. /home)
 * - pageKey provided   → checks canAccessPage(pageKey) from AuthContext
 *                        which reads the user's UserAccess.pages array
 * - Superadmin         → always passes
 */
const ProtectedRoute = ({ children, pageKey }) => {
  const { token, loading, canAccessPage, allowedPages } = useAuth();

  if (loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "60vh" }}>
        <div style={{ textAlign: "center", color: "#888" }}>
          <div style={{ fontSize: 14 }}>Loading…</div>
        </div>
      </div>
    );
  }

  // Not logged in
  if (!token) return <Navigate to="/" replace />;

  // Page requires a specific access key
  if (pageKey && !canAccessPage(pageKey)) {
    // If allowedPages is loaded and not empty, redirect to first allowed page
    if (allowedPages && allowedPages.length > 0) {
      const PAGE_PATHS = {
        dashboard:                '/home',
        user_management:          '/user-management',
        user_form:                '/user-form',
        view_user:                '/view-user',
        edit_user:                '/edit-user',
        access_panel:             '/access-panel',
        master_qualifications:    '/master/qualifications',
        master_sub_qualifications:'/master/sub-qualifications',
        master_equipment:         '/master/equipment',
        master_certifications:    '/master/certifications',
      };
      const firstPage = allowedPages[0];
      const path = PAGE_PATHS[firstPage] || '/home';
      return <Navigate to={path} replace />;
    }
    // No pages allowed at all
    return <Navigate to="/home" replace />;
  }

  return children;
};

export default ProtectedRoute;
